import {
    AfterViewInit,
    Directive,
    ElementRef,
    Input,
    NgZone,
    OnDestroy,
    Renderer2,
} from '@angular/core';
import { ResizeSensor } from 'css-element-queries';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { StickyHeaderService } from './stickyheader.service';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function isIE() {
    const ua = window.navigator.userAgent;
    // eslint-disable-next-line @typescript-eslint/prefer-includes
    return ua.indexOf('Trident/') > -1 || ua.indexOf('Edge/') > -1;
}

@Directive({
    selector: '[stickyTableHeader]',
})
export class StickyHeaderDirective implements AfterViewInit, OnDestroy {
    @Input() overridable: boolean;
    @Input() offsetHeight: any = 0;

    private _eventObs: Observable<any>;
    private _resizeObs: Observable<any>;
    private _indexObs: Observable<number>;
    private initBoundingRect: any;
    public browserIsIE = isIE();

    private resizesub: Subscription;
    private scrollsub: Subscription;
    private indexsub: Subscription;

    private _top: any;
    private _height: any;
    private headerNavHeight: any = 0;
    private tableParent: any;
    private rowParent: any;

    private stickyIndex: number;

    listener: any;

    constructor(
        private readonly _el: ElementRef,
        private readonly _ngZone: NgZone,
        private readonly renderer: Renderer2,
        private readonly _stickyService: StickyHeaderService,
    ) {}

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngAfterViewInit() {
        setTimeout(() => {
            this._ngZone.runOutsideAngular(() => {
                this._resizeObs = fromEvent(window, 'resize');
                this.resizesub = this._resizeObs
                    .pipe(debounceTime(300))
                    .subscribe(
                        () => {
                            this.scrollsub.unsubscribe();

                            window.requestAnimationFrame(() => {
                                this.resizeSticky();
                            });
                        },
                        () => null,
                    );

                this._eventObs = fromEvent(window, 'scroll');
                this.scrollsub = this._eventObs.subscribe(() => {
                    this.handleScroll();
                });
            });

            this.setDimensions();
            this.addParentRow();
            this._indexObs = this._stickyService.indexObservable(
                this.stickyIndex,
            );
            this.indexsub = this._indexObs.subscribe((g) => {
                const parentBoundingRect =
                    this.tableParent[0].getBoundingClientRect();
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                this._top = parentBoundingRect.top + window.pageYOffset;
                this.stickyIndex = g;
            });

            this.listener = new ResizeSensor(
                this._el.nativeElement,
                (dimensions) => {
                    this._height = dimensions.height;
                    this.handleScroll();
                },
            );
        }, 300);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    addParentRow() {
        const parentIsRegistered = this.rowParent.hasClass('sticky-reg');
        this.stickyIndex = this._stickyService.addSticky(
            this.rowParent,
            this.tableParent,
        );
        if (!parentIsRegistered) {
            this.rowParent.addClass('sticky-reg');
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    handleScroll() {
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        const windowY =
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            (window.scrollY || window.pageYOffset) + this.headerNavHeight;

        const table = this.tableParent.get(0);
        if (!table) {
            return;
        }
        const parentBoundingRect = table.getBoundingClientRect();

        const tablePositionYTop = parentBoundingRect.top;
        const tablePositionYBotton = parentBoundingRect.bottom;
        const bottom = tablePositionYBotton - this.headerNavHeight;

        if (
            tablePositionYTop <= this.headerNavHeight &&
            bottom > this._height &&
            !this._stickyService.isInDescendantStickyDomain(
                this.stickyIndex,
                windowY,
            )
        ) {
            this.beSticky(Math.abs(tablePositionYTop - this.headerNavHeight));
        } else {
            this.removeSticky();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setDimensions() {
        this.rowParent = $(this._el.nativeElement).closest('tr');
        this.tableParent = $(this._el.nativeElement).closest('table');

        this.tableParent.css('transform-style', 'preserve-3d');
        this.tableParent.css('-ms-transform-style', 'none');

        const headerNavDOM = document.querySelectorAll('cmps-nav .navbar');
        if (headerNavDOM.length) {
            this.headerNavHeight =
                headerNavDOM[0].getBoundingClientRect().height;
        }

        this.initBoundingRect = this._el.nativeElement.getBoundingClientRect();
        const parentBoundingRect = this.tableParent[0].getBoundingClientRect();

        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        this._top = parentBoundingRect.top + window.pageYOffset;
        this._height = this.initBoundingRect.height;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    resizeSticky() {
        this.removeSticky();
        this.setDimensions();
        this.handleScroll();
        // resubscribe after being unsubscribed by resize event
        if (this.scrollsub) {
            this.scrollsub.unsubscribe();
        }
        this.scrollsub = this._eventObs.subscribe(() => {
            this.handleScroll();
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    beSticky(y: number) {
        if (this.browserIsIE) {
            this.renderer.setStyle(
                this._el.nativeElement,
                '-ms-transform',
                `translateX(0) translateY(${y}px) translateZ(0px)`,
            );
            this.renderer.setStyle(
                this._el.nativeElement,
                '-webkit-transform',
                `translateX(0) translateY(${y}px) translateZ(0px)`,
            );
        } else {
            this.renderer.setStyle(
                this._el.nativeElement,
                'transform',
                `translateX(0) translateY(${y}px) translateZ(0px)`,
            );
        }

        $(this._el.nativeElement).addClass('stickied');

        if (this._height > 45) {
            $(this._el.nativeElement).addClass('higher-before-cover');
        } else {
            $(this._el.nativeElement).removeClass('higher-before-cover');
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    removeSticky() {
        if (this.browserIsIE) {
            this.renderer.removeStyle(this._el.nativeElement, '-ms-transform');
            this.renderer.removeStyle(
                this._el.nativeElement,
                '-webkit-transform',
            );
        } else {
            this.renderer.removeStyle(this._el.nativeElement, 'transform');
        }
        this.tableParent.css('transform-style', 'none');
        $(this._el.nativeElement).removeClass('stickied');
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnDestroy() {
        this._top = undefined;
        this._height = undefined;
        this.headerNavHeight = undefined;
        this.tableParent = undefined;

        if (this.resizesub) {
            this.resizesub.unsubscribe();
        }
        if (this.scrollsub) {
            this.scrollsub.unsubscribe();
        }
        if (this.indexsub) {
            this.indexsub.unsubscribe();
        }

        this._eventObs = undefined;
        this._resizeObs = undefined;
        this._indexObs = undefined;
        this.initBoundingRect = undefined;

        this._stickyService.removeFromStickyList(this.stickyIndex);

        if (this.listener) {
            this.listener.detach();
            this.listener = null;
        }
    }
}
